/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import { Box, Stack, Text, Input, Divider, HStack, Grid, Center, Spinner } from '@chakra-ui/react';
import { useGetVariants } from '@/services/manageProduct';
import { IVariant } from '@/pages/ManageProduct/ActiveProducts';

interface IReStockFormProps {
  product: { id: number } | null;
  variants: IVariant[];
  setVariants: React.Dispatch<React.SetStateAction<IVariant[]>>;
  onSubmit: () => void;
}

const RestockForm = ({ product, variants, setVariants, onSubmit }: IReStockFormProps) => {
  const { data: apiData, isLoading } = useGetVariants(product?.id ?? null);

  const variantsData = Array.isArray(apiData) ? apiData : apiData?.results || [];

  useEffect(() => {
    if (isLoading || variantsData.length === 0) return;

    const initialVariants = variantsData.map((variant: any) => ({
      id: variant.id,
      color: variant.color,
      value: variant.value,
      size: variant.size.value,
      variant_price: parseFloat(variant.variant_price),
      stock_quantity: variant.stock_quantity,
    }));

    setVariants(initialVariants);
  }, [variantsData, isLoading, setVariants]);

  const handleStockUpdate = (e: React.ChangeEvent<HTMLInputElement>, variantId: number) => {
    const value = e.target.value;

    // Allow empty string (user clearing the field)
    if (value === '') {
      setVariants((prev) =>
        prev.map((v) => (v.id === variantId ? { ...v, stock_quantity: 0 } : v)),
      );
      return;
    }

    // Only accept valid numbers
    const updatedQuantity = Number(value);
    if (!isNaN(updatedQuantity) && updatedQuantity >= 0) {
      setVariants((prev) =>
        prev.map((v) => (v.id === variantId ? { ...v, stock_quantity: updatedQuantity } : v)),
      );
    }
  };

  const getInputValue = (variantId: number) => {
    const variant = variants.find((v) => v.id === variantId);
    const currentQty = variant?.stock_quantity ?? 0;
    return currentQty === 0 ? '' : currentQty.toString();
  };

  if (isLoading) {
    return (
      <Center h="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (variantsData.length === 0) {
    return <Text>No variants found for this product.</Text>;
  }

  const groupedByColor = variantsData.reduce((acc: any, variant: any) => {
    if (!acc[variant.color]) {
      acc[variant.color] = {
        color: variant.color,
        value: variant.value,
        sizes: [],
      };
    }
    acc[variant.color].sizes.push({
      id: variant.id,
      size: variant.size,
      stock_quantity: variant.stock_quantity,
      variant_price: variant.variant_price,
    });
    return acc;
  }, {});

  const colorVariants = Object.values(groupedByColor);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Stack>
        {colorVariants.map((variant: any, variantIndex: number) => (
          <Box key={variantIndex} p={4} fontFamily="primary" rounded="md">
            <HStack alignItems="center" mb={4}>
              <Box h={8} w={8} borderRadius="xl" bg={variant.value} />
              <Text fontSize="sm" fontWeight="normal" color="text.secondary">
                {variant.color}
              </Text>
            </HStack>

            <HStack wrap="wrap" spacing={2} mb={4}>
              {variant.sizes.map((sizeObj: any, sizeIndex: number) => {
                const isOutOfStock = sizeObj.stock_quantity === 0;
                return (
                  <Box
                    key={sizeIndex}
                    position="relative"
                    borderRadius="md"
                    px={1}
                    bg={'#FFFFFF'}
                    w={{ base: '50px', md: '60px' }}
                    borderWidth="1px"
                    cursor={isOutOfStock ? 'not-allowed' : 'pointer'}
                    borderColor={isOutOfStock ? 'text.disable' : 'border.default'}
                    color={isOutOfStock ? 'text.disable' : 'text.primary'}
                    textAlign="center"
                    opacity={isOutOfStock ? 0.5 : 1}
                  >
                    {sizeObj.size.value}
                    {isOutOfStock && (
                      <Box
                        position="absolute"
                        top={0}
                        right={0}
                        width="100%"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        pointerEvents="none"
                      >
                        <Box
                          width="108%"
                          height="1px"
                          bg="text.disable"
                          transform="rotate(-20deg)"
                          transformOrigin="center"
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </HStack>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} columnGap={6} rowGap={6}>
              {variant.sizes.map((size: any) => (
                <Box key={size.id} display="flex" alignItems="center" gap={2}>
                  <Box>
                    <Text color={'text.secondary'} fontSize="sm">
                      Restock
                    </Text>
                    <Text
                      fontWeight="semibold"
                      border="1px solid #D0D5DD"
                      borderRadius="lg"
                      w="50px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg={'#FFFFFF'}
                      boxShadow="0px 1px 2px 0px #1018280D"
                      px={3}
                      py={2}
                      fontSize="md"
                    >
                      {size.size.value}
                    </Text>
                  </Box>
                  <Box>
                    <Text color={'text.secondary'} fontSize="sm">
                      Quantity
                    </Text>
                    <Input
                      type="number"
                      bg={'#FFFFFF'}
                      placeholder={`Restock for ${size.size.value}`}
                      width={{ base: '100%', md: '200px' }}
                      px={3}
                      py={2}
                      fontSize="md"
                      min="0"
                      value={getInputValue(size.id)}
                      onChange={(e) => handleStockUpdate(e, size.id)}
                    />
                  </Box>
                </Box>
              ))}
            </Grid>

            {colorVariants.length > 1 && variantIndex < colorVariants.length - 1 && (
              <Divider mt={4} />
            )}
          </Box>
        ))}
      </Stack>
    </form>
  );
};

export default RestockForm;
